import { SeatSelectionBusModal, SeatSelectionTrainModal } from '@distribusion/owl-seats'
import { useField, useFormikContext } from 'formik'
import React, { useCallback, useMemo, useState } from 'react'

import { assertNotNull } from '@lib/assert'
import connectionUtils from '@lib/connection'
import seatsUtils from '@lib/seatSelection'
import utils from '@lib/utils'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { OpenModalFn, SeatSelectionContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatSelection'
import { useActiveSegments } from '@pages/Checkout/SeatSelection/NewSeats/hooks/useActiveSegments'
import { useSeatValidation } from '@pages/Checkout/SeatSelection/NewSeats/hooks/useSeatValidation'
import { useSelectedSeats } from '@pages/Checkout/SeatSelection/NewSeats/hooks/useSelectedSeats'
import useSeatsQueries from '@queries/seats'
import useTwoStepSeatsQuery from '@queries/seats/twoSteps'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'

interface WithModalsProps {
  enabled: boolean
  children: (isLoading: boolean) => React.ReactNode
}

const getLayoutSegment = ({ segment }: Seat.Data) => segment

export const WithSingleStepSeatModal: React.FC<WithModalsProps> = ({ enabled, children }) => {
  const [{ seatSelection }] = useSettings()
  const [{ outbound, inbound }] = useCheckout()
  const {
    values: { passengers, fareClass, vacancy, meta },
  } = useFormikContext<CheckoutFormData>()
  const { seats, setSeats } = useSelectedSeats(enabled)
  const connections = useMemo(() => ({ outbound, inbound }), [outbound, inbound])
  // TODO: Use correct fare for return trip: https://distribusion.atlassian.net/browse/OWL-3957
  const tripFareClasses = useMemo(() => ({ outbound: fareClass, inbound: fareClass }), [fareClass])

  const [loadedSeats, { isLoading }] = useSeatsQueries({
    connections: utils.array.compact([outbound, inbound]),
    enabled: vacancy?.vacant,
    // istanbul ignore next
    options: { passengers, fareClass: seatSelection.sendFareClass ? fareClass : null, cards: meta.cards },
  })

  const layout = useMemo(
    () => ({
      inbound: [],
      outbound: [],
      ...utils.array.groupBy(loadedSeats, seat => (seat.connection.id === inbound?.id ? 'inbound' : 'outbound')),
    }),
    [inbound?.id, loadedSeats],
  )

  const trainSeats = useMemo(() => seatsUtils.transformIntoTrainSeats(layout), [layout])
  const schemeForActiveSegments = useActiveSegments(trainSeats.scheme)
  const layoutForActiveSegments = useActiveSegments(layout, getLayoutSegment)

  const validate = useSeatValidation(enabled, trainSeats.scheme)
  useField({ name: 'seats', validate })

  const [currentSegment, setCurrentSegment] = useState<{
    direction: Seat.TripDirection
    segment: Segment
  }>()

  const handleConfirmSelection = useCallback(
    ({ seats }: Seat.SubmitData): void => {
      setSeats(seats)
      setCurrentSegment(currentValue => {
        if (currentValue?.direction === 'outbound' && inbound) {
          const firstInbound = schemeForActiveSegments.inbound[0]
          // istanbul ignore next
          if (firstInbound) {
            return {
              direction: 'inbound',
              segment: inbound.segments.find(segment => connectionUtils.isSameSegment(segment, firstInbound))!,
            }
          }
        }

        return undefined
      })
    },
    [inbound, schemeForActiveSegments.inbound, setSeats],
  )

  const currentTrainSegment = useMemo(
    () =>
      currentSegment && {
        ...currentSegment,
        segment: trainSeats.scheme[currentSegment.direction].find(segment =>
          connectionUtils.isSameSegment(segment, currentSegment.segment),
        )!,
      },
    [currentSegment, trainSeats.scheme],
  )

  const handleSegmentChange = useCallback(
    (direction: Seat.TripDirection, segment: Seat.Train.Segment | Seat.Segment) => {
      const connection = connections[direction]
      assertNotNull(connection, 'connection not found for direction')
      const connectionSegment = connection.segments.find(connectionSegment =>
        connectionUtils.isSameSegment(connectionSegment, segment),
      )!
      setCurrentSegment({
        direction,
        segment: connectionSegment,
      })
    },
    [connections],
  )

  const openModal: OpenModalFn = useCallback(
    (direction, segment) => setCurrentSegment(direction && segment && { direction, segment }),
    [],
  )

  return (
    <SeatSelectionContext.Provider
      value={{
        openModal,
        seats,
        setSeats,
        scheme: trainSeats.scheme,
        layoutQuery: trainSeats.layoutQuery,
      }}
    >
      {children(isLoading)}
      {currentSegment && (
        <>
          {currentSegment.segment.vehicle?.vehicleType?.code !== 'TRAIN' ? (
            <SeatSelectionBusModal
              initialSelection={seats}
              layout={layoutForActiveSegments}
              connections={{ outbound, inbound }}
              currentSegment={currentSegment}
              onSegmentChange={handleSegmentChange}
              reservedSeatsCount={passengers.length}
              initialFare={fareClass}
              opened
              onClose={() => setCurrentSegment(undefined)}
              fareFilter={fareClass}
              onSubmit={handleConfirmSelection}
              outboundSeatsCount={passengers.length}
              passengers={passengers}
            />
          ) : (
            <SeatSelectionTrainModal
              scheme={schemeForActiveSegments}
              layoutQuery={trainSeats.layoutQuery}
              currentSegment={currentTrainSegment}
              onSegmentChange={handleSegmentChange as any}
              allowSkip
              opened
              initialSelection={seats}
              reservedSeatsCount={passengers.length}
              onSubmit={handleConfirmSelection}
              onClose={() => setCurrentSegment(undefined)}
              connection={currentSegment.direction === 'inbound' ? inbound! : outbound!}
              selectedFares={tripFareClasses}
            />
          )}
        </>
      )}
    </SeatSelectionContext.Provider>
  )
}

export const WithTwoStepSeatsModal: React.FC<WithModalsProps> = ({ enabled, children }) => {
  const [{ outbound, inbound }] = useCheckout()
  const {
    values: { passengers, fareClass, vacancy, meta },
  } = useFormikContext<CheckoutFormData>()
  // TODO: Use correct fare for return trip: https://distribusion.atlassian.net/browse/OWL-3957
  const tripFareClasses = useMemo(() => ({ outbound: fareClass, inbound: fareClass }), [fareClass])

  const { layoutQuery, preselectedSeats, scheme, isLoading } = useTwoStepSeatsQuery({
    connections: utils.object.compact({ outbound, inbound }),
    enabled: vacancy?.vacant,
    options: { fareClass, passengers, cards: meta.cards },
  })

  const schemeForActiveSegments = useActiveSegments(scheme)
  const { seats, setSeats } = useSelectedSeats(enabled, preselectedSeats)

  const validate = useSeatValidation(enabled, scheme)
  useField({ name: 'seats', validate })

  const [currentSegment, setCurrentSegment] = useState<{
    direction: Seat.TripDirection
    segment: Seat.Train.Segment
  }>()

  // istanbul ignore next
  const resetSeatSelection = useCallback(() => {
    setSeats(null)
    setCurrentSegment(undefined)
  }, [setSeats])

  // istanbul ignore next
  const handleConfirmSelection = useCallback(
    ({ seats }: Seat.SubmitData): void => {
      setSeats(seats)

      setCurrentSegment(currentValue =>
        currentValue?.direction === 'outbound' && inbound
          ? { direction: 'inbound', segment: scheme.inbound[0] }
          : undefined,
      )
    },
    [setSeats, inbound, scheme.inbound],
  )
  // istanbul ignore next
  const openModal = useCallback(
    (direction?: Seat.TripDirection, segment?: Segment) => {
      return setCurrentSegment(
        direction &&
          segment && {
            direction: direction,
            segment: scheme[direction].find(schemeSegment => connectionUtils.isSameSegment(schemeSegment, segment))!,
          },
      )
    },
    [scheme],
  )
  // istanbul ignore next
  const handleSegmentChange = useCallback((direction: Seat.TripDirection, segment: Seat.Train.Segment) => {
    setCurrentSegment({ direction, segment })
  }, [])

  return (
    <SeatSelectionContext.Provider
      value={{
        openModal,
        seats,
        setSeats,
        preselectedSeats,
        scheme,
        layoutQuery,
      }}
    >
      {children(isLoading)}
      {currentSegment && (
        <SeatSelectionTrainModal
          initialSelection={seats ?? preselectedSeats}
          currentSegment={currentSegment}
          onSegmentChange={handleSegmentChange as any}
          layoutQuery={layoutQuery}
          scheme={schemeForActiveSegments}
          opened
          reservedSeatsCount={passengers.length}
          onSubmit={handleConfirmSelection}
          onClose={() => setCurrentSegment(undefined)}
          connection={currentSegment.direction === 'inbound' ? inbound! : outbound!}
          selectedFares={tripFareClasses}
          onClear={resetSeatSelection}
        />
      )}
    </SeatSelectionContext.Provider>
  )
}

export const WithSeatsModal: React.FC<WithModalsProps> = ({ enabled: externallyEnabled, children }) => {
  const [{ seatSelection }] = useSettings()
  const {
    values: { vacancy },
  } = useFormikContext<CheckoutFormData>()
  const enabled = Boolean(seatSelection.enabledOnCheckout && vacancy?.vacant && externallyEnabled)

  return seatSelection.flowType === 'two_step' ? (
    <WithTwoStepSeatsModal enabled={enabled}>{children}</WithTwoStepSeatsModal>
  ) : (
    <WithSingleStepSeatModal enabled={enabled}>{children}</WithSingleStepSeatModal>
  )
}
